import type {
  MrActivitySection,
  MrAlert,
  MrAvailableMonth,
  MrChannel,
  MrCurrentMonth,
  MrHero,
  MrKeywordMove,
  MrPatientOutcomes,
  MrRevenuePoint,
  MrSeoStat,
} from './types';

export const MR_CURRENT_MONTH: MrCurrentMonth = {
  label: 'April 2026',
  status: 'month_to_date',
  daysElapsed: 18,
  daysTotal: 30,
};

export const MR_AVAILABLE_MONTHS: MrAvailableMonth[] = [
  { label: 'April 2026', status: 'month_to_date' },
  { label: 'March 2026', status: 'finalized' },
  { label: 'February 2026', status: 'finalized' },
  { label: 'January 2026', status: 'finalized' },
  { label: 'December 2025', status: 'finalized' },
  { label: 'November 2025', status: 'finalized' },
];

export const MR_HERO: MrHero = {
  adSpend: 3400,
  revenue: 78900,
  roi: 23.2,
  newPatients: 24,
  lastMonth: { adSpend: 3100, revenue: 62400, roi: 20.1, newPatients: 19 },
};

export const MR_PATIENT_OUTCOMES: MrPatientOutcomes = {
  newPatients: { value: 24, lastMonth: 19, change: 26 },
  totalAppointments: { value: 87, lastMonth: 71, change: 23 },
  projectedLTV: { value: 201600, lastMonth: 146300, change: 38 },
  revenueByService: [
    { service: 'Dental Implants', revenue: 33600, patients: 8, tone: 'lav' },
    { service: 'Invisalign', revenue: 24500, patients: 5, tone: 'success' },
    { service: 'Emergency / Walk-in', revenue: 9800, patients: 6, tone: 'terra' },
    { service: 'Cosmetic (Veneers)', revenue: 7200, patients: 3, tone: 'gold' },
    { service: 'Preventive / Hygiene', revenue: 3800, patients: 2, tone: 'neutral' },
  ],
};

export const MR_CHANNELS: MrChannel[] = [
  {
    id: 'google_ads',
    name: 'Google Ads',
    tone: 'info',
    icon: 'ads',
    summary: '9 active campaigns · Emergency Dental campaign driving 38% of bookings',
    metrics: [
      { label: 'Ad Spend', value: '$3,400' },
      { label: 'Revenue', value: '$68.2K', tone: 'success' },
      { label: 'Calls', value: '247' },
      { label: 'Form Fills', value: '78' },
      { label: 'Booked', value: '21' },
      { label: 'CTR', value: '4.8%' },
    ],
  },
  {
    id: 'seo',
    name: 'Organic SEO',
    tone: 'success',
    icon: 'seo',
    summary: '2 articles reached page 1 this month · Building long-term visibility',
    metrics: [
      { label: 'Cost', value: '$0', tone: 'success' },
      { label: 'Organic Visitors', value: '2,140' },
      { label: 'Articles Published', value: '6' },
      { label: 'New Page-1 Rankings', value: '2' },
      { label: 'Keywords Improved', value: '28' },
    ],
    note: "Revenue attribution for organic SEO isn't reliable — users often convert through other channels after discovering you organically. The value here is visibility and long-term asset building.",
  },
  {
    id: 'gbp',
    name: 'Google Business Profile',
    tone: 'gold',
    icon: 'gbp',
    summary: 'Avg Map Pack position improved from #4 to #3 · 14 reviews responded to',
    metrics: [
      { label: 'Cost', value: '$0', tone: 'success' },
      { label: 'Map Pack Views', value: '8,420' },
      { label: 'Profile Actions', value: '412' },
      { label: 'Direction Requests', value: '68' },
      { label: 'Reviews Replied', value: '14' },
      { label: 'Avg Rating', value: '4.7' },
    ],
  },
];

export const MR_REVENUE_TREND: MrRevenuePoint[] = [
  { month: "Nov '25", revenue: 42100 },
  { month: "Dec '25", revenue: 51300 },
  { month: "Jan '26", revenue: 54800 },
  { month: "Feb '26", revenue: 58200 },
  { month: "Mar '26", revenue: 62400 },
  { month: "Apr '26", revenue: 78900, isCurrent: true },
];

export const MR_ACTIVITY_SECTIONS: MrActivitySection[] = [
  {
    id: 'campaigns',
    title: 'Campaign Optimization',
    items: [
      { label: 'Campaigns actively managed', value: '9' },
      { label: 'Landing page variants tested', value: '32' },
      { label: 'A/B tests concluded', value: '7' },
      { label: 'Winning variants deployed', value: '5' },
      { label: 'Budget reallocations', value: '14' },
    ],
  },
  {
    id: 'seo',
    title: 'SEO & Content',
    items: [
      { label: 'Articles published', value: '6' },
      { label: 'Articles reaching page 1', value: '2' },
      { label: 'Keywords improved (moved up)', value: '28' },
      { label: 'New backlinks earned', value: '4' },
      { label: 'Schema deployments', value: '3' },
    ],
  },
  {
    id: 'gbp',
    title: 'Google Business Profile',
    items: [
      { label: 'GBP posts published', value: '12' },
      { label: 'Photos added (synced to Google)', value: '18' },
      { label: 'Reviews responded to', value: '14' },
      { label: 'Q&As answered', value: '6' },
      { label: 'Profile field updates', value: '3' },
    ],
  },
  {
    id: 'intel',
    title: 'Market Intelligence',
    items: [
      { label: 'Competitor profiles refreshed', value: '8' },
      { label: 'Market sentiment reports', value: '4' },
      { label: 'New threats detected & addressed', value: '2' },
      { label: 'Competitor offer changes tracked', value: '11' },
    ],
  },
];

export const MR_TOTAL_ACTIONS = '4,287';

export const MR_KEYWORD_MOVEMENTS: MrKeywordMove[] = [
  { keyword: 'dental implants manhattan', from: 14, to: 6, change: 8 },
  { keyword: 'emergency dentist nyc', from: 8, to: 3, change: 5 },
  { keyword: 'invisalign manhattan', from: 22, to: 11, change: 11 },
  { keyword: 'pediatric dentist midtown', from: 19, to: 10, change: 9 },
  { keyword: 'cosmetic veneers nyc', from: 31, to: 34, change: -3 },
];

export const MR_SEO_STATS: MrSeoStat[] = [
  { key: 'articles', label: 'Articles Published', value: '6', note: 'Total: 48 articles live' },
  { key: 'page1', label: 'Keywords on Page 1', value: '18', change: 3, suffix: '', note: '+3 new page-1 rankings' },
  { key: 'visitors', label: 'Organic Visitors', value: '2,140', change: 18, note: 'From Google search' },
  { key: 'photos', label: 'GBP Photo Coverage', value: '76%', change: 12, note: '18 new photos synced' },
];

export const MR_ALERTS: MrAlert[] = [
  {
    type: 'warning',
    title: 'Cosmetic Veneers campaign needs monitoring',
    detail:
      'CPC rose 18% this month due to new competitor. Cost per patient increased from $98 to $113. ELVA shifted to long-tail keywords and is monitoring for 5 more days before suggesting budget changes.',
  },
];
